import { ContactAddress } from "client/Api/Email/contacts";
import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { SectionGrid } from "./SectionGrid";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { Anchor } from "client/Components";
import { Section, SectionEmptyBehavior } from "./Section";

export type AddressSectionProps = {
	homeAddress: ContactAddress | null;
	businessAddress: ContactAddress | null;
	otherAddress: ContactAddress | null;
	emptyBehavior: SectionEmptyBehavior;
};

export const AddressSection = (props: AddressSectionProps): ReactElement => {
	const {
		homeAddress,
		businessAddress,
		otherAddress,
		emptyBehavior
	} = props;

	return (
		<Section
			title={localize("_Addresses")}
			trackingId="addresses"
			emptyBehavior={emptyBehavior}
			isEmpty={
				!isNotEmpty(homeAddress)
				&& !isNotEmpty(businessAddress)
				&& !isNotEmpty(otherAddress)
			}
		>
			<SectionGrid>
				{isNotEmpty(homeAddress) && (
					<SectionGrid.Row
						label={localize("_HomeAddress")}
						trackingId="home-address"
						icon="house"
					>
						<Address address={homeAddress}/>
					</SectionGrid.Row>
				)}
				{isNotEmpty(businessAddress) && (
					<SectionGrid.Row
						label={localize("_WorkAddress")}
						trackingId="business-address"
						icon="buildings"
					>
						<Address address={businessAddress}/>
					</SectionGrid.Row>
				)}
				{isNotEmpty(otherAddress) && (
					<SectionGrid.Row
						label={localize("_OtherAddress")}
						trackingId="other-address"
						icon="location-dot"
					>
						<Address address={otherAddress} />
					</SectionGrid.Row>
				)}
			</SectionGrid>
		</Section>
	);
};
AddressSection.displayName = "AddressSection";

type AddressProps = {
	address: ContactAddress;
};

const Address = (props: AddressProps): ReactElement => {
	const {
		address
	} = props;

	const {
		street,
		postcode,
		state,
		city,
		country
	} = address;

	const cityAddress: string | null = combineAddressSegments(street, postcode, city);
	const addressLink: string | null = generateGoogleMapsUrl(address);
	const addressLinkLevel: AddressBlockLinkLevel | undefined = getLinkLevel([
		[cityAddress, "city"],
		[state, "state"],
		[country, "country"]
	]);

	const renderValue = (
		value: string,
		level: AddressBlockLinkLevel
	): ReactElement | string => {
		if (!addressLink) {
			return value;
		}

		if (level !== addressLinkLevel) {
			return value;
		}

		return (
			<Anchor
				href={addressLink}
				target="_blank"
			>
				{value}
			</Anchor>
		);
	};

	return (
		<>
			{isNotNil(cityAddress) && (
				<SectionGrid.Value
					label={localize("_Address")}
					trackingId="city-address"
					valueToCopy={cityAddress}
				>
					{renderValue(cityAddress, "city")}
				</SectionGrid.Value>
			)}
			{isNotNil(state) && (
				<SectionGrid.Value
					label={localize("_StateProvince")}
					trackingId="state"
					valueToCopy={state}
				>
					{renderValue(state, "state")}
				</SectionGrid.Value>
			)}
			{isNotNil(country) && (
				<SectionGrid.Value
					label={localize("_CountryRegion")}
					trackingId="country"
					valueToCopy={country}
				>
					{renderValue(country, "country")}
				</SectionGrid.Value>
			)}
		</>
	);
};
Address.displayName = "Address";

function generateGoogleMapsUrl(address: ContactAddress): string | null {
	const {
		street,
		city,
		postcode,
		state,
		country
	} = address;

	const query: string | null = combineAddressSegments(
		street,
		city,
		postcode,
		state,
		country
	);

	if (!query) {
		return null;
	}

	return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function combineAddressSegments(...segments: readonly (string | null)[]): string | null {
	const nonEmptySegments = segments.filter(isNotNil);

	if (nonEmptySegments.length === 0) {
		return null;
	}

	return nonEmptySegments
		.join(", ");
}

type AddressBlockLinkLevel = "city" | "state" | "country";

function getLinkLevel(addresses: readonly ([string | null, AddressBlockLinkLevel])[]): AddressBlockLinkLevel | undefined {
	return addresses.filter(a => isNotNil(a[0]))
		.map(a => a[1])[0];
};

function isNotEmpty(address: ContactAddress | null): address is ContactAddress {
	if (!address) {
		return false;
	}

	const {
		street,
		city,
		postcode,
		state,
		country
	} = address;

	return isNotNilOrEmpty(street)
		|| isNotNilOrEmpty(city)
		|| isNotNilOrEmpty(postcode)
		|| isNotNilOrEmpty(state)
		|| isNotNilOrEmpty(country);
}